import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Recommendation {
  type: string;
  severity: 'high' | 'medium' | 'low';
  resourceId: string;
  resourceName: string;
  title: string;
  description: string;
  currentCost: number;
  optimizedCost: number;
  savings: number;
  savingsPercentage: number;
  effort: string;
  impact: string;
  provider: string;
  suggestedSku?: string;
  suggestedTier?: string;
  warning?: string;
  applicability?: string;
}

interface OptimizationData {
  currentMonthlyCost: number;
  optimizedMonthlyCost: number;
  totalSavings: number;
  savingsPercentage: number;
  recommendations: Recommendation[];
  summary: {
    totalRecommendations: number;
    byType: { [key: string]: number };
    bySeverity: { high: number; medium: number; low: number };
  };
}

interface CostOptimizationRecommendationsProps {
  optimizationData: OptimizationData | null;
  currency?: string;
  loading?: boolean;
}

export const CostOptimizationRecommendations: React.FC<CostOptimizationRecommendationsProps> = ({
  optimizationData,
  currency = 'USD',
  loading = false,
}) => {
  const { t } = useTranslation('finops');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner} />
          <p>{t('optimization.loading')}</p>
        </div>
      </div>
    );
  }

  if (!optimizationData) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>
          <p>{t('optimization.noData')}</p>
        </div>
      </div>
    );
  }

  const formatCost = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getSeverityColor = (severity: string): string => {
    const colors = {
      high: '#dc2626',
      medium: '#ea580c',
      low: '#65a30d',
    };
    return colors[severity as keyof typeof colors] || '#6b7280';
  };

  const getSeverityLabel = (severity: string): string => {
    const labels: { [key: string]: string } = {
      high: t('optimization.severity.high'),
      medium: t('optimization.severity.medium'),
      low: t('optimization.severity.low'),
    };
    return labels[severity] || severity;
  };

  const getEffortLabel = (effort: string): string => {
    const effortLower = effort.toLowerCase();
    const labels: { [key: string]: string } = {
      low: t('optimization.effort.low'),
      faible: t('optimization.effort.low'),
      medium: t('optimization.effort.medium'),
      moyen: t('optimization.effort.medium'),
      high: t('optimization.effort.high'),
      élevé: t('optimization.effort.high'),
      elevé: t('optimization.effort.high'),
    };
    return labels[effortLower] || effort;
  };

  const getImpactLabel = (impact: string): string => {
    const impactLower = impact.toLowerCase();
    const labels: { [key: string]: string } = {
      low: t('optimization.impact.low'),
      faible: t('optimization.impact.low'),
      medium: t('optimization.impact.medium'),
      moyen: t('optimization.impact.medium'),
      high: t('optimization.impact.high'),
      élevé: t('optimization.impact.high'),
      elevé: t('optimization.impact.high'),
    };
    return labels[impactLower] || impact;
  };

  const getTypeIcon = (type: string): string => {
    const icons: { [key: string]: string } = {
      downsize: '📉',
      reserved_instance: '🔒',
      spot_instance: '⚡',
      storage_tier: '💾',
      region: '🌍',
      autoscaling: '📊',
      shutdown_schedule: '⏰',
      rightsizing: '⚖️',
    };
    return icons[type] || '💡';
  };

  const filteredRecommendations = optimizationData.recommendations.filter((rec) => {
    if (filterSeverity === 'all') return true;
    return rec.severity === filterSeverity;
  });

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div style={styles.container}>
      {/* En-tête avec résumé */}
      <div style={styles.header}>
        <h2 style={styles.title}>{t('optimization.title')}</h2>

        <div style={styles.summary}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>{t('optimization.currentCost')}</div>
            <div style={styles.summaryValue}>
              {formatCost(optimizationData.currentMonthlyCost)}/{t('costBreakdown.perMonth')}
            </div>
          </div>

          <div style={styles.summaryArrow}>→</div>

          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>{t('optimization.optimizedCost')}</div>
            <div style={{ ...styles.summaryValue, color: '#10b981' }}>
              {formatCost(optimizationData.optimizedMonthlyCost)}/{t('costBreakdown.perMonth')}
            </div>
          </div>

          <div style={styles.savingsCard}>
            <div style={styles.savingsLabel}>{t('optimization.potentialSavings')}</div>
            <div style={styles.savingsValue}>
              {formatCost(optimizationData.totalSavings)}/{t('costBreakdown.perMonth')}
            </div>
            <div style={styles.savingsPercentage}>
              {optimizationData.savingsPercentage.toFixed(1)}% {t('optimization.reduction')}
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques rapides */}
      <div style={styles.stats}>
        <div style={styles.statItem}>
          <span style={styles.statValue}>{optimizationData.summary.totalRecommendations}</span>
          <span style={styles.statLabel}>{t('optimization.recommendationsCount')}</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue, color: getSeverityColor('high') }}>
            {optimizationData.summary.bySeverity.high}
          </span>
          <span style={styles.statLabel}>{t('optimization.highPriority')}</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue, color: getSeverityColor('medium') }}>
            {optimizationData.summary.bySeverity.medium}
          </span>
          <span style={styles.statLabel}>{t('optimization.mediumPriority')}</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue, color: getSeverityColor('low') }}>
            {optimizationData.summary.bySeverity.low}
          </span>
          <span style={styles.statLabel}>{t('optimization.lowPriority')}</span>
        </div>
      </div>

      {/* Filtres */}
      <div style={styles.filters}>
        <button
          style={{
            ...styles.filterButton,
            ...(filterSeverity === 'all' ? styles.filterButtonActive : {}),
          }}
          onClick={() => setFilterSeverity('all')}
        >
          {t('optimization.all')} ({optimizationData.recommendations.length})
        </button>
        <button
          style={{
            ...styles.filterButton,
            ...(filterSeverity === 'high' ? styles.filterButtonActive : {}),
          }}
          onClick={() => setFilterSeverity('high')}
        >
          {t('optimization.high')} ({optimizationData.summary.bySeverity.high})
        </button>
        <button
          style={{
            ...styles.filterButton,
            ...(filterSeverity === 'medium' ? styles.filterButtonActive : {}),
          }}
          onClick={() => setFilterSeverity('medium')}
        >
          {t('optimization.medium')} ({optimizationData.summary.bySeverity.medium})
        </button>
        <button
          style={{
            ...styles.filterButton,
            ...(filterSeverity === 'low' ? styles.filterButtonActive : {}),
          }}
          onClick={() => setFilterSeverity('low')}
        >
          {t('optimization.low')} ({optimizationData.summary.bySeverity.low})
        </button>
      </div>

      {/* Liste des recommandations */}
      <div style={styles.recommendationsList}>
        {filteredRecommendations.map((rec) => {
          const isExpanded = expandedId === rec.resourceId + rec.type;

          return (
            <div
              key={rec.resourceId + rec.type}
              style={styles.recommendationCard}
            >
              <div
                style={styles.recommendationHeader}
                onClick={() => toggleExpand(rec.resourceId + rec.type)}
              >
                <div style={styles.recommendationLeft}>
                  <span style={styles.typeIcon}>{getTypeIcon(rec.type)}</span>
                  <div>
                    <div style={styles.recommendationTitle}>{rec.title}</div>
                    <div style={styles.recommendationResource}>{rec.resourceName}</div>
                  </div>
                </div>

                <div style={styles.recommendationRight}>
                  <div style={styles.savingsBadge}>
                    <div style={styles.savingsAmount}>
                      {formatCost(rec.savings)}
                    </div>
                    <div style={styles.savingsPercent}>
                      -{rec.savingsPercentage}%
                    </div>
                  </div>

                  <div
                    style={{
                      ...styles.severityBadge,
                      backgroundColor: getSeverityColor(rec.severity) + '20',
                      color: getSeverityColor(rec.severity),
                    }}
                  >
                    {getSeverityLabel(rec.severity)}
                  </div>

                  <div style={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</div>
                </div>
              </div>

              {isExpanded && (
                <div style={styles.recommendationDetails}>
                  <p style={styles.description}>{rec.description}</p>

                  <div style={styles.detailsGrid}>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>{t('optimization.details.currentCost')}</span>
                      <span style={styles.detailValue}>{formatCost(rec.currentCost)}/{t('costBreakdown.perMonth')}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>{t('optimization.details.optimizedCost')}</span>
                      <span style={{ ...styles.detailValue, color: '#10b981' }}>
                        {formatCost(rec.optimizedCost)}/{t('costBreakdown.perMonth')}
                      </span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>{t('optimization.details.effort')}</span>
                      <span style={styles.detailValue}>{getEffortLabel(rec.effort)}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>{t('optimization.details.impact')}</span>
                      <span style={styles.detailValue}>{getImpactLabel(rec.impact)}</span>
                    </div>
                  </div>

                  {rec.suggestedSku && (
                    <div style={styles.suggestion}>
                      <strong>{t('optimization.details.suggestedSku')}</strong> {rec.suggestedSku}
                    </div>
                  )}

                  {rec.suggestedTier && (
                    <div style={styles.suggestion}>
                      <strong>{t('optimization.details.suggestedTier')}</strong> {rec.suggestedTier}
                    </div>
                  )}

                  {rec.warning && (
                    <div style={styles.warning}>
                      ⚠️ {rec.warning}
                    </div>
                  )}

                  {rec.applicability && (
                    <div style={styles.applicability}>
                      📌 {rec.applicability}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredRecommendations.length === 0 && (
        <div style={styles.empty}>
          <p>{t('optimization.noRecommendations')}</p>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '48px',
    color: '#6b7280',
  },
  spinner: {
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  empty: {
    textAlign: 'center',
    padding: '48px',
    color: '#6b7280',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    margin: 0,
    marginBottom: '16px',
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
  },
  summary: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  summaryCard: {
    flex: '1 1 150px',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  summaryLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '4px',
  },
  summaryValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#111827',
  },
  summaryArrow: {
    fontSize: '24px',
    color: '#9ca3af',
  },
  savingsCard: {
    flex: '1 1 200px',
    padding: '16px',
    backgroundColor: '#ecfdf5',
    borderRadius: '8px',
    border: '2px solid #10b981',
  },
  savingsLabel: {
    fontSize: '12px',
    color: '#047857',
    marginBottom: '4px',
  },
  savingsValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#10b981',
  },
  savingsPercentage: {
    fontSize: '13px',
    color: '#059669',
    marginTop: '4px',
  },
  stats: {
    display: 'flex',
    gap: '24px',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#111827',
  },
  statLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '4px',
  },
  filters: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filterButton: {
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    transition: 'all 0.2s',
  },
  filterButtonActive: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    borderColor: '#3b82f6',
  },
  recommendationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  recommendationCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s',
  },
  recommendationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    cursor: 'pointer',
    backgroundColor: '#ffffff',
    transition: 'background-color 0.2s',
  },
  recommendationLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  typeIcon: {
    fontSize: '24px',
  },
  recommendationTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#111827',
  },
  recommendationResource: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '2px',
  },
  recommendationRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  savingsBadge: {
    textAlign: 'right',
  },
  savingsAmount: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#10b981',
  },
  savingsPercent: {
    fontSize: '12px',
    color: '#059669',
  },
  severityBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
  },
  expandIcon: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  recommendationDetails: {
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderTop: '1px solid #e5e7eb',
  },
  description: {
    margin: 0,
    marginBottom: '16px',
    fontSize: '14px',
    color: '#374151',
    lineHeight: '1.6',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '12px',
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailLabel: {
    fontSize: '13px',
    color: '#6b7280',
  },
  detailValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
  },
  suggestion: {
    padding: '8px 12px',
    backgroundColor: '#eff6ff',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#1e40af',
    marginTop: '8px',
  },
  warning: {
    padding: '8px 12px',
    backgroundColor: '#fef3c7',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#92400e',
    marginTop: '8px',
  },
  applicability: {
    padding: '8px 12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#374151',
    marginTop: '8px',
  },
};
